import axios from 'axios'
import {getAdminToken} from '../auth/_requests'

const API_URL = process.env.REACT_APP_API_URL

const adminApi = axios.create({withCredentials: true})
adminApi.interceptors.request.use((config) => {
  const token = getAdminToken()
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export interface WaAccount {
  _id: string
  first_name?: string
  last_name?: string
  company_name?: string
  email?: string
  username?: string
  phone?: string
}

// A conversation and its contact are the same row — WaContact on the server.
export interface WaConversation {
  _id: string
  phone: string
  name: string
  provider: string
  phone_number_id: string
  business_number: string
  account: WaAccount | null
  // How the thread was linked: 'code' | 'phone' | 'manual' | '' (older rows).
  account_source?: string
  code: string
  last_message_at: string | null
  last_message_text: string
  last_direction: 'in' | 'out'
  last_inbound_at: string | null
  // False once 24h have passed since the contact's last inbound message — Meta
  // only delivers free text inside that window, so the composer locks.
  window_open: boolean
  unread_count: number
  message_count: number
  notes: string
  archived: boolean
  created_at: string
}

export interface WaMessage {
  _id: string
  direction: 'in' | 'out'
  wamid: string
  type: string
  body: string
  media: {url?: string; mime_type?: string; filename?: string; caption?: string} | null
  status: 'received' | 'queued' | 'sent' | 'delivered' | 'read' | 'failed'
  error: string
  sent_by_name: string
  code: string
  sent_at: string
  created_at: string
}

export interface WaStats {
  contacts: number
  unread: number
  messages: number
  inbound_24h: number
  outbound_24h: number
  unassigned: number
  can_send: boolean
  phone_number_id: string
  account_id: string
}

export type WaFilter = 'all' | 'unread' | 'unassigned' | 'archived'

export function listConversations(params: {search?: string; filter?: WaFilter; page?: number; limit?: number}) {
  return adminApi.get<{
    ok: boolean
    conversations: WaConversation[]
    total: number
    unread_total: number
    page: number
    pages: number
    can_send: boolean
  }>(`${API_URL}/admin/whatsapp/conversations`, {params})
}

export function listMessages(id: string, params?: {limit?: number; before?: string}) {
  return adminApi.get<{
    ok: boolean
    contact: WaConversation
    messages: WaMessage[]
    has_more: boolean
    can_send: boolean
  }>(`${API_URL}/admin/whatsapp/conversations/${id}/messages`, {params})
}

export function sendMessage(id: string, message: string) {
  return adminApi.post<{ok: boolean; message: WaMessage; error?: string}>(
    `${API_URL}/admin/whatsapp/conversations/${id}/send`,
    {message}
  )
}

// One attachment (image / video / audio / document), with an optional caption.
// The server puts the file in public storage and sends it by URL — WhatsApp only
// fetches media from a public link.
export function sendAttachment(id: string, file: File, caption: string) {
  const form = new FormData()
  form.append('file', file)
  if (caption) form.append('caption', caption)
  return adminApi.post<{ok: boolean; message: WaMessage; error?: string}>(
    `${API_URL}/admin/whatsapp/conversations/${id}/send-media`,
    form
  )
}

export function markRead(id: string) {
  return adminApi.post<{ok: boolean}>(`${API_URL}/admin/whatsapp/conversations/${id}/read`, {})
}

export function updateConversation(
  id: string,
  patch: {name?: string; notes?: string; archived?: boolean; account?: string | null}
) {
  return adminApi.patch<{ok: boolean; conversation: WaConversation}>(
    `${API_URL}/admin/whatsapp/conversations/${id}`,
    patch
  )
}

export function listContacts(params: {search?: string; page?: number; limit?: number}) {
  return adminApi.get<{ok: boolean; contacts: WaConversation[]; total: number; page: number; pages: number}>(
    `${API_URL}/admin/whatsapp/contacts`,
    {params}
  )
}

export function getStats() {
  return adminApi.get<{ok: boolean} & WaStats>(`${API_URL}/admin/whatsapp/stats`)
}

// The customer behind a thread: who they are and whether their setup is
// actually running, so an agent can answer without opening three other screens.
export interface WaContext {
  account: WaAccount | null
  stats: any | null
  open_tickets: number
  campaigns: number
  campaign_list?: {_id: string; name: string; enabled: boolean}[]
  profile_list?: {_id: string; name: string}[]
}

// One thing an agent is allowed to change on a customer's account. The list is
// closed and comes from the server — the UI never invents an action.
export interface AgentActionDef {
  key: string
  label: string
  target: 'profile' | 'campaign' | 'account'
}

// A recorded run. `before` is what the fields held immediately before the write,
// which is what makes the undo a restore rather than a guess.
export interface AgentActionRecord {
  _id: string
  action: string
  summary: string
  target_label: string
  before: any
  absent_fields?: string[]
  after: any
  status: 'applied' | 'reverted' | 'failed'
  error: string
  triggered_by_name: string
  created_at: string
  reverted_at: string | null
  reverted_by_name: string
}

export function listAgentActions() {
  return adminApi.get<{ok: boolean; actions: AgentActionDef[]}>(`${API_URL}/admin/whatsapp/agent/actions`)
}

export function getAgentLog(id: string) {
  return adminApi.get<{ok: boolean; actions: AgentActionRecord[]}>(
    `${API_URL}/admin/whatsapp/conversations/${id}/agent/log`
  )
}

export function runAgentAction(id: string, action: string, targetId?: string) {
  return adminApi.post<{ok: boolean; action?: {summary: string}; error?: string}>(
    `${API_URL}/admin/whatsapp/conversations/${id}/agent/run`,
    {action, target_id: targetId}
  )
}

export function revertAgentAction(actionId: string) {
  return adminApi.post<{ok: boolean; error?: string}>(
    `${API_URL}/admin/whatsapp/agent/actions/${actionId}/revert`,
    {}
  )
}

export function getContext(id: string) {
  return adminApi.get<{ok: boolean} & WaContext>(`${API_URL}/admin/whatsapp/conversations/${id}/context`)
}

export function loginAsCustomer(id: string) {
  return adminApi.post<{ok: boolean; api_token: string; error?: string}>(
    `${API_URL}/admin/whatsapp/conversations/${id}/login-as`,
    {}
  )
}

export function openTicket(
  id: string,
  body: {subject: string; description?: string; department?: string; task_type?: string}
) {
  return adminApi.post<{ok: boolean; ticket: {_id: string; ticket_number: string; subject: string}; error?: string}>(
    `${API_URL}/admin/whatsapp/conversations/${id}/ticket`,
    body
  )
}

// AI-drafted replies. Drafting only — the result lands in the composer for a
// human to edit and send; nothing is sent automatically.
// An agent action the AI proposed for this conversation — validated server-side
// against the closed action list and this customer's own profiles/campaigns, with
// the live before/after of every field it would write. Nothing has run yet.
export interface SuggestedAgentAction {
  action: string
  label: string
  target: 'account' | 'profile' | 'campaign'
  target_id: string
  target_label: string
  changes: {field: string; before: any; after: any}[]
  reason: string
}

export function suggestReplies(id: string) {
  return adminApi.post<{
    ok: boolean
    summary: string
    intent: string
    // For the admin only — what the account data says is really going on.
    diagnosis: string
    suggestions: string[]
    suggested_action: SuggestedAgentAction | null
    error?: string
  }>(`${API_URL}/admin/whatsapp/conversations/${id}/suggest`, {})
}

// Source URL for a message's image/document, served by the admin media proxy.
// The admin JWT rides as a query param because an <img src> cannot carry an
// Authorization header — the same fallback auth_middleware already accepts for
// SSE. The PayCall API token never leaves the server; only this proxy holds it.
export function mediaUrl(messageId: string): string {
  return `${API_URL}/admin/whatsapp/media/${messageId}?token=${encodeURIComponent(getAdminToken() || '')}`
}

export function searchAccounts(search: string) {
  return adminApi.get<{ok: boolean; accounts: WaAccount[]}>(`${API_URL}/admin/whatsapp/accounts`, {
    params: {search},
  })
}

// --- Chat grid (all of today's conversations at once) ---------------------------

export interface WaGridIssue {
  _id: string
  title: string
  status: 'open' | 'in_progress' | 'done'
  account_status: 'open' | 'in_progress' | 'done'
  urgency: 'low' | 'normal' | 'high' | 'urgent'
  waiting: 'none' | 'aryeh' | 'support'
  due_date: string
}

// One tile. `contact` is null for a customer with open issues who never wrote
// to the support line — there is no thread to show or reply into.
export interface WaGridCard {
  contact: WaConversation | null
  account: WaAccount | null
  today: boolean
  issues: WaGridIssue[]
  suggestions: WaSuggestion[]
}

// A reply the Grok Bot proposed. Suggest-only — sending is always a human click.
export interface WaSuggestion {
  _id: string
  code: string
  text: string
  confidence: number
  reasoning: string
  intent: string
  needs_human: boolean
  created_at: string
}

export function getGrid(includeIssues: boolean) {
  return adminApi.get<{ok: boolean; day_start: string; cards: WaGridCard[]; can_send: boolean}>(
    `${API_URL}/admin/whatsapp/grid`,
    {params: includeIssues ? {include_issues: 1} : {}}
  )
}

export function suggestionFeedback(code: string, body: {status: 'used' | 'edited' | 'dismissed'; final_text?: string; note?: string}) {
  return adminApi.post<{ok: boolean}>(`${API_URL}/admin/whatsapp/suggestions/${encodeURIComponent(code)}/feedback`, body)
}

export function askGrokbot(chatId: string) {
  return adminApi.post<{ok: boolean; error?: string}>(`${API_URL}/admin/whatsapp/conversations/${chatId}/ask-grokbot`, {})
}

export function createGridIssue(body: {
  account_id: string
  title: string
  description: string
  route: 'support' | 'aryeh'
  urgency: 'low' | 'normal' | 'high' | 'urgent'
}) {
  return adminApi.post<{ok: boolean; issue_id: string; error?: string}>(`${API_URL}/admin/whatsapp/grid/issue`, body)
}
